import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import { MessageCircle, Phone } from 'lucide-react';
import api from '../utils/api';
import BottomNav from '../components/BottomNav';
import PanelToast from '../components/PanelToast';
import KpiStrip from '../components/KpiStrip';
import PanelSection from '../components/PanelSection';
import PanelEmptyState from '../components/PanelEmptyState';
import ConfirmDialog from '../components/ConfirmDialog';

export default function RequestsPage() {
  const [requests, setRequests] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showActivity, setShowActivity] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [toast, setToast] = useState(null);
  const [actionResult, setActionResult] = useState(null); // {sms_text, phone}
  const [processing, setProcessing] = useState(null); // id
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [newHint, setNewHint] = useState(0);
  const [query, setQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const previousCountRef = useRef(0);

  const fetchRequests = useCallback(async (initialLoad = false) => {
    try {
      const [requestsResponse, activityResponse] = await Promise.all([
        api.get('/requests?status=pending'),
        api.get('/requests/activity'),
      ]);

      const data = requestsResponse.data;
      const nextCount = Array.isArray(data) ? data.length : 0;
      const previousCount = previousCountRef.current;

      if (!initialLoad && nextCount > previousCount) {
        setNewHint(nextCount - previousCount);
      }

      previousCountRef.current = nextCount;
      setRequests(data);
      setActivities(Array.isArray(activityResponse.data) ? activityResponse.data : []);
      setFetchError('');
    } catch (err) {
      console.error(err);
      setFetchError('Nie udalo sie pobrac najnowszych prosb. Sprobuj ponownie.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => requests.some((req) => req.id === id)));
  }, [requests]);

  useEffect(() => {
    fetchRequests(true);

    const intervalId = setInterval(() => {
      fetchRequests(false);
    }, 10000);

    function handleVisibilityChange() {
      if (!document.hidden) {
        fetchRequests(false);
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchRequests]);

  async function performRequestAction(id, action) {
    const { data } = await api.put(`/requests/${id}/${action}`);
    return data;
  }

  async function handleAccept(id) {
    setProcessing(id);
    try {
      const data = await performRequestAction(id, 'accept');
      setActionResult({ sms_text: data.sms_text, phone: data.phone, type: 'accept' });
      setToast({ type: 'success', message: 'Prośba została zaakceptowana.' });
      await fetchRequests(false);
    } catch (err) {
      setToast({
        type: 'error',
        message: err.response?.data?.error || 'Nie udało się zaakceptować prośby.',
      });
    } finally {
      setProcessing(null);
    }
  }

  async function handleReject(id) {
    setProcessing(id);
    try {
      const data = await performRequestAction(id, 'reject');
      setActionResult({ sms_text: data.sms_text, phone: data.phone, type: 'reject' });
      setToast({ type: 'success', message: 'Prośba została odrzucona.' });
      await fetchRequests(false);
    } catch (err) {
      setToast({
        type: 'error',
        message: err.response?.data?.error || 'Nie udało się odrzucić prośby.',
      });
    } finally {
      setProcessing(null);
    }
  }

  async function handleBulkAction(action) {
    if (selectedIds.length === 0) {
      setToast({ type: 'error', message: 'Najpierw zaznacz co najmniej jedną prośbę.' });
      return;
    }

    setConfirmState({ action });
  }

  async function confirmBulkAction(action) {
    setBulkProcessing(true);

    try {
      const results = await Promise.allSettled(
        selectedIds.map((id) => performRequestAction(id, action))
      );

      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const errorCount = results.length - successCount;

      if (successCount > 0) {
        setToast({
          type: errorCount > 0 ? 'info' : 'success',
          message:
            errorCount > 0
              ? `${successCount} próśb przetworzono, ${errorCount} nie udało się zmienić.`
              : action === 'accept'
                ? `Zaakceptowano ${successCount} próśb.`
                : `Odrzucono ${successCount} próśb.`,
        });
      } else {
        setToast({ type: 'error', message: 'Nie udało się przetworzyć zaznaczonych próśb.' });
      }

      setSelectedIds([]);
      await fetchRequests(false);
    } catch (err) {
      setToast({ type: 'error', message: 'Wystąpił błąd podczas akcji seryjnej.' });
    } finally {
      setBulkProcessing(false);
    }
  }

  function handleSMS(phone, text) {
    window.location.href = `sms:${phone}?body=${encodeURIComponent(text)}`;
  }

  function handleCall(phone) {
    window.location.href = `tel:${phone}`;
  }

  const serviceOptions = useMemo(() => {
    const uniqueServices = new Map();
    for (const req of requests) {
      uniqueServices.set(req.service_name, req.service_name);
    }
    return Array.from(uniqueServices.keys()).sort((a, b) => a.localeCompare(b, 'pl'));
  }, [requests]);

  const visibleRequests = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const next = requests.filter((req) => {
      const queryMatch =
        !normalizedQuery ||
        req.client_name.toLowerCase().includes(normalizedQuery) ||
        req.client_phone.toLowerCase().includes(normalizedQuery);

      const serviceMatch = serviceFilter === 'all' || req.service_name === serviceFilter;
      return queryMatch && serviceMatch;
    });

    next.sort((a, b) => {
      if (sortBy === 'oldest') {
        return new Date(a.created_at) - new Date(b.created_at);
      }
      if (sortBy === 'slot-soonest') {
        return new Date(a.requested_slot_start) - new Date(b.requested_slot_start);
      }
      if (sortBy === 'slot-latest') {
        return new Date(b.requested_slot_start) - new Date(a.requested_slot_start);
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return next;
  }, [requests, query, serviceFilter, sortBy]);

  const allVisibleSelected = visibleRequests.length > 0 && visibleRequests.every((req) => selectedIds.includes(req.id));
  const requestKpis = [
    { key: 'requests-total', label: 'Oczekujące', value: requests.length, featured: true },
    { key: 'requests-visible', label: 'Widoczne', value: visibleRequests.length, tone: 'neutral' },
    { key: 'requests-selected', label: 'Zaznaczone', value: selectedIds.length, tone: bulkMode ? 'accent' : 'soft' },
    { key: 'requests-activity', label: 'Ostatnie akcje', value: activities.length, tone: 'neutral' },
  ];

  function toggleSelected(id) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }

  function toggleSelectAllVisible() {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleRequests.some((req) => req.id === id)));
      return;
    }

    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleRequests.map((req) => req.id)])));
  }

  return (
    <div className="panel-page requests-page">
      <PanelToast toast={toast} onClose={() => setToast(null)} />

      <div className="panel-header">
        <div className="panel-header-copy">
          <span className="panel-eyebrow">Nowe zgłoszenia</span>
          <h1 className="panel-title">Prośby o wizytę</h1>
        </div>
      </div>

      <div className="requests-page-layout">
        <div className="requests-page-summary">
          <KpiStrip
            items={requestKpis}
            variant="stats"
            className="requests-kpi-strip"
            ariaLabel="Podsumowanie próśb"
          />

          {newHint > 0 && (
            <div className="alert alert-success request-live-alert" role="status">
              Wpadły nowe prośby: +{newHint}
              <button className="btn-ghost request-live-alert-btn" onClick={() => setNewHint(0)}>
                OK
              </button>
            </div>
          )}

          {fetchError && (
            <div className="alert alert-error request-live-alert" role="alert">
              {fetchError}
              <button
                className="btn-ghost request-live-alert-btn"
                onClick={() => fetchRequests(false)}
              >
                Ponów
              </button>
            </div>
          )}
        </div>

        <PanelSection
          className="request-toolbar requests-page-filters"
          title="Filtry i akcje"
          actions={(
            <div className="request-bulk-head">
              <button
                className={`btn-secondary request-bulk-toggle ${bulkMode ? 'request-bulk-toggle--active' : ''}`}
                onClick={() => {
                  setBulkMode((prev) => !prev);
                  setSelectedIds([]);
                }}
                type="button"
              >
                {bulkMode ? 'Zamknij tryb seryjny' : 'Tryb seryjny'}
              </button>
              {bulkMode && (
                <button className="btn-ghost" type="button" onClick={toggleSelectAllVisible}>
                  {allVisibleSelected ? 'Odznacz widoczne' : 'Zaznacz widoczne'}
                </button>
              )}
            </div>
          )}
        >
          <div className="form-group request-toolbar-search">
            <label htmlFor="requestSearch">Szukaj klientki</label>
            <input
              id="requestSearch"
              type="text"
              placeholder="Imię lub numer telefonu"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="request-toolbar-row">
            <div className="form-group request-toolbar-field">
              <label htmlFor="serviceFilter">Usługa</label>
              <select
                id="serviceFilter"
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
              >
                <option value="all">Wszystkie</option>
                {serviceOptions.map((serviceName) => (
                  <option key={serviceName} value={serviceName}>
                    {serviceName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group request-toolbar-field">
              <label htmlFor="sortBy">Sortowanie</label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Najnowsze zgłoszenia</option>
                <option value="oldest">Najstarsze zgłoszenia</option>
                <option value="slot-soonest">Najszybszy termin wizyty</option>
                <option value="slot-latest">Najpóźniejszy termin wizyty</option>
              </select>
            </div>
          </div>

          {bulkMode && (
            <div className="bulk-actions-bar">
              <div className="bulk-actions-meta">Zaznaczono: {selectedIds.length}</div>
              <div className="bulk-actions-buttons">
                <button
                  className="btn-secondary"
                  type="button"
                  disabled={bulkProcessing || selectedIds.length === 0}
                  onClick={() => handleBulkAction('reject')}
                >
                  {bulkProcessing ? 'Przetwarzanie...' : 'Odrzuć zaznaczone'}
                </button>
                <button
                  className="btn-primary"
                  type="button"
                  disabled={bulkProcessing || selectedIds.length === 0}
                  onClick={() => handleBulkAction('accept')}
                >
                  {bulkProcessing ? 'Przetwarzanie...' : 'Akceptuj zaznaczone'}
                </button>
              </div>
            </div>
          )}
        </PanelSection>
      </div>

      {/* Wynik akcji – gotowy SMS */}
      {actionResult && (
        <div className={`action-result action-result--${actionResult.type}`}>
          <p className="action-result-title">
            {actionResult.type === 'accept' ? 'Zaakceptowano' : 'Odrzucono'}
          </p>
          <p className="action-result-sms">{actionResult.sms_text}</p>
          <div className="action-result-btns">
            <button
              className="btn-primary"
              onClick={() => handleSMS(actionResult.phone, actionResult.sms_text)}
            >
              Wyślij SMS
            </button>
            <button
              className="btn-secondary"
              onClick={() => handleCall(actionResult.phone)}
            >
              Zadzwoń
            </button>
            <button className="btn-ghost" onClick={() => setActionResult(null)}>
              Zamknij
            </button>
          </div>
        </div>
      )}

      {loading && <p className="loading-text">Ładowanie...</p>}

      {!loading && requests.length === 0 && !actionResult && (
        <PanelEmptyState
          icon="🎉"
          title="Brak nowych próśb"
          description="Na ten moment wszystko jest obsłużone. Kolejne zgłoszenia pojawią się tutaj automatycznie."
          actions={(
            <button className="btn-secondary" type="button" onClick={() => fetchRequests(false)}>
              Odśwież listę
            </button>
          )}
        />
      )}

      {!loading && requests.length > 0 && visibleRequests.length === 0 && (
        <PanelEmptyState
          icon="🔎"
          title="Brak wyników dla filtrów"
          description="Zmień usługę, sortowanie albo wyszukiwaną klientkę, żeby zobaczyć więcej zgłoszeń."
          className="panel-empty-state--compact"
          actions={(
            <button
              className="btn-secondary"
              onClick={() => {
                setQuery('');
                setServiceFilter('all');
                setSortBy('newest');
              }}
            >
              Wyczyść filtry
            </button>
          )}
        />
      )}

      <PanelSection className="requests-page-primary" title={`Lista próśb (${visibleRequests.length})`}>
        <div className="request-list">
          {visibleRequests.map((req) => {
            const slotDate = parseISO(req.requested_slot_start);
            const createdAtDate = parseISO(req.created_at);
            const isProcessing = processing === req.id;

            return (
              <div key={req.id} className="request-card">
                <div className="request-card-body">
                  <div className="request-main">
                    {bulkMode && (
                      <label className="request-select-box request-select-box--card">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(req.id)}
                          onChange={() => toggleSelected(req.id)}
                        />
                      </label>
                    )}
                    <div className="request-details">
                      <div className="request-client-stack">
                        <div className="request-name">{req.client_name}</div>
                        <div className="request-phone">{req.client_phone}</div>
                      </div>
                      <div className="request-service">{req.service_name}</div>
                      <div className="request-slot">
                        {format(slotDate, "EEEE d MMMM 'o' HH:mm", { locale: pl })}
                      </div>
                      <div className="request-meta">
                        Wysłano: {format(createdAtDate, "d.MM.yyyy 'o' HH:mm", { locale: pl })}
                      </div>
                      <div className="request-price">{req.price} zł · {req.duration_minutes} min</div>
                      {Array.isArray(req.selected_addons) && req.selected_addons.length > 0 && (
                        <div className="request-price">Dodatki: {req.selected_addons.map((addon) => addon.name).join(', ')}</div>
                      )}
                    </div>
                  </div>
                  <div className="request-contact-btns">
                    <button className="icon-btn" onClick={() => handleCall(req.client_phone)}>
                      <Phone size={16} strokeWidth={1.9} />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => handleSMS(req.client_phone, `Cześć ${req.client_name}!`)}
                    >
                      <MessageCircle size={16} strokeWidth={1.9} />
                    </button>
                  </div>
                </div>

                <div className="request-btns">
                  <button
                    className="btn-accept"
                    disabled={isProcessing || bulkProcessing}
                    onClick={() => handleAccept(req.id)}
                  >
                    {isProcessing ? '...' : '✓ Akceptuj'}
                  </button>
                  <button
                    className="btn-reject"
                    disabled={isProcessing || bulkProcessing}
                    onClick={() => handleReject(req.id)}
                  >
                    {isProcessing ? '...' : '✕ Odrzuć'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </PanelSection>

      {!loading && activities.length > 0 && (
        <PanelSection
          className="activity-feed activity-feed--compact requests-page-activity"
          title="Ostatnie akcje"
          actions={(
            <button
              className="btn-ghost activity-toggle-btn"
              onClick={() => setShowActivity((prev) => !prev)}
            >
              {showActivity ? 'Ukryj' : 'Pokaż'}
            </button>
          )}
          aria-label="Ostatnie akcje"
        >
          {showActivity && (
            <div className="activity-list activity-list--scrollable">
              {activities.slice(0, 10).map((item) => {
                const createdAtDate = parseISO(item.created_at);
                return (
                  <div key={item.id} className="activity-item">
                    <div className="activity-message">{item.message}</div>
                    <div className="activity-time">
                      {format(createdAtDate, "d.MM.yyyy 'o' HH:mm", { locale: pl })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </PanelSection>
      )}

      <ConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.action === 'accept' ? 'Zaakceptować zaznaczone prośby?' : 'Odrzucić zaznaczone prośby?'}
        description={confirmState?.action === 'accept'
          ? `Za chwilę zaakceptuję ${selectedIds.length} zaznaczonych próśb.`
          : `Za chwilę odrzucę ${selectedIds.length} zaznaczonych próśb.`}
        confirmLabel={confirmState?.action === 'accept' ? 'Akceptuj' : 'Odrzuć'}
        cancelLabel="Anuluj"
        confirmTone={confirmState?.action === 'reject' ? 'danger' : 'primary'}
        onCancel={() => setConfirmState(null)}
        onConfirm={() => {
          const action = confirmState?.action;
          setConfirmState(null);
          if (action) {
            void confirmBulkAction(action);
          }
        }}
      />

      <BottomNav active="requests" />
    </div>
  );
}
